import { useRef, useEffect, useState } from 'react';

/* ─── Data ─────────────────────────────────────────────────────── */
const stats = [
  { value: '5+', label: 'Projects Delivered', icon: 'ri-stack-line' },
  { value: '2+', label: 'Enterprise Clients', icon: 'ri-building-2-line' },
  { value: '5+', label: 'Years Experience', icon: 'ri-time-line' },
  { value: '24/7', label: 'Support Coverage', icon: 'ri-customer-service-2-line' },
];

/* ─── Hooks ─────────────────────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function About() {
  const { ref: heroTextRef, visible: heroTextVisible } = useInView(0.12);
  const { ref: heroImgRef, visible: heroImgVisible } = useInView(0.12);

  return (
    <section
      id="about"
      className="relative overflow-hidden"
      style={{ background: '#FAFBFF' }}
    >
      {/* ── Decorative bg elements ──────────────────────────── */}
      <div
        className="absolute top-0 left-0 w-full h-px pointer-events-none"
        style={{ background: 'linear-gradient(to right, transparent, rgba(59,130,246,0.18), transparent)' }}
      />
      <div
        className="absolute -top-60 -right-60 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.045) 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(10,31,68,0.035) 0%, transparent 70%)' }}
      />

      {/* ════════════════════════════════════════════════════
          PART 1 — About / Story
      ════════════════════════════════════════════════════ */}
      <div className="py-24 md:py-32 relative z-10">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_480px] gap-12 lg:gap-20 items-center">

            {/* Left column */}
            <div
              ref={heroTextRef}
              style={{
                opacity: heroTextVisible ? 1 : 0,
                transform: heroTextVisible ? 'none' : 'translateY(30px)',
                transition: 'opacity 0.9s ease, transform 0.9s ease',
              }}
            >
              <span
                className="inline-flex items-center gap-2 text-[#3B82F6] text-xs font-bold tracking-[3.5px] uppercase mb-5"
              >
                <span className="w-6 h-px bg-[#3B82F6]" />
                About nexaurA Infinity
              </span>
              <h2
                className="text-4xl md:text-[52px] font-bold text-[#0A1F44] mb-6 leading-[1.13]"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Connecting Machines,<br />
                Data &{' '}
                <span
                  className="relative inline-block"
                  style={{
                    background: 'linear-gradient(135deg, #0369A1 0%, #0EA5E9 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Decisions
                </span>
              </h2>
              <p className="text-[#0A1F44]/65 text-[15px] md:text-[16px] leading-relaxed mb-10 max-w-[520px]">
                nexaurA Infinity delivers intelligent systems that bridge the gap between complex industrial operations and smart digital infrastructure. We partner with businesses worldwide to implement AI, automation, and vision systems that make operations faster, smarter, and more resilient.
              </p>

              {/* Vision / Mission blocks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
                <div
                  className="relative p-5 rounded-2xl overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(14,165,233,0.06) 100%)',
                    border: '1px solid rgba(59,130,246,0.15)',
                  }}
                >
                  <div
                    className="absolute top-0 left-0 w-1 h-full rounded-l-2xl"
                    style={{ background: 'linear-gradient(to bottom, #3B82F6, #0EA5E9)' }}
                  />
                  <div className="pl-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-eye-2-line text-[#3B82F6] text-sm" />
                      </div>
                      <span className="text-[#3B82F6] text-[10px] font-black tracking-[3px] uppercase">Vision</span>
                    </div>
                    <p className="text-[#0A1F44]/80 text-[13px] leading-relaxed">
                      Globally trusted partner in intelligent automation — empowering every industry to operate at its highest potential.
                    </p>
                  </div>
                </div>

                <div
                  className="relative p-5 rounded-2xl overflow-hidden"
                  style={{
                    background: 'rgba(10,31,68,0.03)',
                    border: '1px solid rgba(10,31,68,0.09)',
                  }}
                >
                  <div
                    className="absolute top-0 left-0 w-1 h-full rounded-l-2xl"
                    style={{ background: 'linear-gradient(to bottom, rgba(10,31,68,0.3), rgba(10,31,68,0.15))' }}
                  />
                  <div className="pl-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-focus-3-line text-[#0A1F44]/50 text-sm" />
                      </div>
                      <span className="text-[#0A1F44]/45 text-[10px] font-black tracking-[3px] uppercase">Mission</span>
                    </div>
                    <p className="text-[#0A1F44]/75 text-[13px] leading-relaxed">
                      Simplify complex operations using AI, making intelligent technology accessible and impactful for businesses at every scale.
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats row */}
              <div
                className="grid grid-cols-4 gap-2 sm:gap-4 pt-8"
                style={{ borderTop: '1px solid rgba(10,31,68,0.08)' }}
              >
                {stats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className="text-center"
                    style={{
                      opacity: heroTextVisible ? 1 : 0,
                      transform: heroTextVisible ? 'none' : 'translateY(16px)',
                      transition: `opacity 0.7s ease ${400 + i * 80}ms, transform 0.7s ease ${400 + i * 80}ms`,
                    }}
                  >
                    <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5">
                      <i className={`${stat.icon} text-[#3B82F6] text-base`} />
                    </div>
                    <div
                      className="text-2xl md:text-3xl font-bold text-[#0A1F44] leading-none"
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-[10px] text-[#0A1F44]/50 font-medium mt-1 leading-tight">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — image + floating cards */}
            <div
              ref={heroImgRef}
              className="relative mt-8 lg:mt-0"
              style={{
                opacity: heroImgVisible ? 1 : 0,
                transform: heroImgVisible ? 'none' : 'translateX(36px)',
                transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
              }}
            >
              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(10,31,68,0.13)' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20global%20AI%20technology%20team%20collaborating%20in%20sleek%20modern%20glass%20office%20with%20panoramic%20city%20skyline%20multiple%20screens%20showing%20data%20analytics%20dashboards%20world%20map%20visualizations%20warm%20ambient%20blue%20accent%20lighting%20diverse%20international%20team%20enterprise%20environment%20highly%20professional&width=700&height=860&seq=about-hero-v3&orientation=portrait"
                  alt="nexaurA Infinity global team"
                  className="w-full h-auto object-cover object-top"
                  loading="lazy"
                  style={{ maxHeight: '540px' }}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(160deg, transparent 50%, rgba(10,31,68,0.22) 100%)' }}
                />
              </div>




            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
